<template>
<div class='h-pos-rel'
     :class='{
         "fw-multiselect-color": colorSelect,
         "fw-select-auto-height": autoHeight,
         "fw-hide-right-icon": !showRightIcon,
         "fw-select-with-icon": withCircles,
         "fw-select-disabled": isDisabled
     }'>
    <span v-if='$slots.title'
          class='fw-multiselect__title'
          :class='`fw-multiselect__title--${type}`'>
        <slot name='title'></slot>
    </span>
    <multiselect
        v-model='localModel'
        class='fw-select qa-multiselect'
        :class='className'
        :options='getOptions'
        :maxHeight='600'
        :disabled='isDisabled'
        :label='label || localLabel'
        selectLabel=''
        selectedLabel=''
        :placeholder='propsPlaceholder'
        :searchable='searchable'
        deselectLabel=''
        :name='getName'
        :openDirection='openDirection'
        :clearOnSelect='clearable'
        :allowEmpty='!canNotDelete'
        :loading='loading'
        :internalSearch='internalSearch'
        :closeOnSelect='multiple ? false : propsCloseOnSelect'
        :clear-on-select='closeOnSelect'
        :group-values='groupValues'
        :group-label='groupLabel'
        :group-select='groupSelect'
        :track-by='trackBy'
        @open='addListenerForScroll'
        @close='onBlur'
        @select='onSelect'
        @input='onInput'
        @search-change='onChange'
        @remove='onDeselect'>
        <template v-if='multiple'
                  slot='option'
                  slot-scope='props'>
            <div class='multiselect-option__checkbox'>
                <FwCheckbox
                    v-model='model'
                    :value='props.option'>
                </FwCheckbox>
                {{ props.option }}
            </div>
        </template>
        <template slot='option'
                  slot-scope='props'>
            <slot name='option' :props='props'>
                <div v-if='colorSelect'
                     :style='{backgroundColor: props.option}'
                     class='fw-select-color__block'>
                </div>
                <div v-else-if='isPhoneWithCodeType'
                     class='fw-select-country-code'>
                    <img :src='props.option.flag'
                         class='fw-select-country-code__image'>
                    <div class='fw-select-country-code__prefix'>
                        {{ props.option.prefix }}
                    </div>
                </div>
            </slot>
        </template>

        <template slot='caret'>
            <template v-if='showRightIcon'>
                <div :style='{backgroundColor: value}'
                     class='fw-select-color__block fw-select-color__selected'>
                </div>
                <div v-if='leftSideIcon' class='fw-select__right--icon'>
                    <FwIcon class='h-flex-center h-pointer'
                            :icon='leftSideIcon'
                            size='24'
                            color='#213F6B'
                            @click-native='remove(option)'>
                    </FwIcon>
                </div>
                <div class='fw-select__caret' :class='{ "fw-select__caret--rotate": !searchIcon}'>
                    <div v-if='isRemoveBySelect'>
                        <FwIcon v-if='!inputValue && !model && !existedValue'
                                class='h-flex-center h-pointer'
                                icon='loupe'
                                size='11'
                                color='#BEC7D4'>
                        </FwIcon>
                        <FwIcon
                            v-else
                            class='h-flex-center h-pointer'
                            icon='close'
                            size='11'
                            color='#BEC7D4'
                            @click.native='removeValue'>
                        </FwIcon>
                    </div>
                    <FwIcon v-else-if='searchIcon'
                            class='h-flex-center h-pointer'
                            icon='loupe'
                            size='11'
                            color='#BEC7D4'>
                    </FwIcon>
                    <div v-else>
                        <FwIcon
                            v-if='getInputValue'
                            class='h-flex-center h-pointer'
                            icon='close'
                            size='11'
                            color='#BEC7D4'
                            @click-native='remove(option)'>
                        </FwIcon>
                        <FwIcon
                            v-else
                            class='h-flex-center'
                            :icon='defaultIcon'
                            size='11'
                            color='#213F6B'>
                        </FwIcon>
                    </div>
                </div>
            </template>
        </template>

        <template v-if='multiple'
                  slot='placeholder'>
            <div v-if='multiple && !getMultipleValue.length' class='h-blue-color h-pr-50'>
                {{ this.propsPlaceholder }}
            </div>
        </template>

        <template v-if='localModel && isPhoneWithCodeType' slot='singleLabel'>
            <div class='fw-select-country-code'>
                <img :src='localModel.flag'
                     class='fw-select-country-code__image'>
                <div class='fw-select-country-code__prefix'>
                    {{ localModel.prefix }}
                </div>
            </div>
        </template>
        <template v-else-if='multiple' slot='singleLabel'>
            <div class='h-blue-color h-pr-50 h-text-overflow-ellipsis'>
                {{ getMultipleValue.length ? getMultipleValue : this.propsPlaceholder }}
            </div>
        </template>

        <template slot='noOptions'>
            <slot name='noOptions'>
                No options
            </slot>
        </template>

        <span slot='noResult'>
            <slot name='noResult'>
                No results
            </slot>
            <template v-if='!$slots.noResult'>
                {{ noElementsFoundText }}
            </template>
        </span>
    </multiselect>
</div>
</template>

<script lang="ts">
import {
    Component, Mixins, Prop, Watch, Emit,
} from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import FwFieldMixin from './interfaces/FwFieldMixin';

const AVAILABLE_TYPES: Array<string> = ['form', 'filter', 'white', 'sort-simple', 'phone-with-code'];

interface CountriesType {
    [key: string]: { name: string, prefix: string, locale: string, flag: string }
}

interface countriesOptionsType {
    id: string,
    name: string,
    prefix: string,
    locale: string,
    flag: string
}

@Component({
    components: {
        Multiselect,
    },
})
export default class FwSelect extends Mixins(FwFieldMixin) {
    localModel: any = '';
    inputValue: any = '';
    countries: CountriesType = {};

    /**
     * Array of available options
     * */
    @Prop({ type: Array, default: () => [] }) readonly options!: Array<Object>;

    /**
     * Boolean which determines whether the select is multiple
     * */
    @Prop({ type: Boolean, default: false }) readonly loading!: boolean;
    @Prop({ type: Boolean, default: false }) readonly multiple!: boolean;
    @Prop({ type: Boolean, default: false }) readonly pagination!: boolean;
    @Prop({ type: Boolean, default: false }) readonly autoHeight!: boolean;

    @Prop({ type: Boolean, default: true }) readonly searchable!: boolean;
    @Prop({ type: Boolean, default: false }) readonly tryToUseSubstring!: boolean;
    @Prop({ type: Boolean, default: false }) readonly colorSelect!: boolean;
    @Prop({ type: Boolean, default: true }) readonly showRightIcon!: boolean;

    @Prop({ type: Boolean, default: false }) readonly groupSelect!: boolean;
    @Prop({ type: String, default: '' }) readonly groupValues!: string;
    @Prop({ type: String, default: '' }) readonly groupLabel!: string;
    @Prop({ type: String, default: '' }) readonly trackBy!: string;

    @Prop({ type: Boolean, default: false }) readonly searchIcon!: boolean;
    @Prop({ type: Boolean, default: false }) readonly isRemoveBySelect!: boolean;
    @Prop({ type: String, default: null }) readonly leftSideIcon!: string | null;
    @Prop({ type: String, default: null }) readonly openDirection!: string | null;
    @Prop({ type: Number, default: null }) readonly paginationPerPage!: number | null;
    @Prop({ type: Boolean, default: false }) readonly canNotDelete!: boolean;
    @Prop({ type: Boolean, default: false }) readonly withCircles!: boolean;
    @Prop({ type: Boolean, default: false }) readonly closeOnSelect!: boolean;
    @Prop({ type: Boolean, default: true }) readonly propsCloseOnSelect!: boolean;
    @Prop({ type: Boolean, default: true }) readonly internalSearch!: boolean;
    @Prop({ type: Boolean, default: false }) readonly existedValue!: boolean;
    @Prop({ type: String, default: 'fr' }) readonly phoneWithCodeStartValue!: string;
    @Prop({ type: String, default: 'arrow-bottom' }) readonly defaultIcon!: string;
    @Prop({ type: Array<String>, default: null }) readonly locales!: Array<string>;

    /**
     * Label from option Object, that will be visible in the dropdown.
     * */
    @Prop(String) readonly label!: string;
    @Prop({ type: String, default: '' }) readonly propsPlaceholder!: string;
    @Prop({ type: String, default: '' }) readonly phoneNumber!: string;
    @Prop({ type: String, default: 'No elements found. Consider changing the search query.' }) readonly noElementsFoundText!: string;
    // @ts-ignore
    @Prop({ type: String, default: 'form', validator: (type: string) => AVAILABLE_TYPES.includes(type) }) readonly type!: string;

    @Watch('model')
    localModelHandler(value: any) {
        this.localModel = value;
    }

    get getOptions() {
        return this.options && this.options.length ? this.options : this.localOptions;
    }

    get countriesOptions(): Array<countriesOptionsType> {
        const options: Array<countriesOptionsType> = [];
        for (const [key, value] of Object.entries(this.countries)) {
            options.push({
                id: key,
                ...value,
            });
        }
        return options;
    }

    get localOptions() {
        return this.isPhoneWithCodeType ? this.countriesOptions : [];
    }

    get localLabel() {
        return this.isPhoneWithCodeType ? 'prefix' : null;
    }

    get isDisabled(): boolean {
        return this.FwField.disabled;
    }
    get className() {
        return `fw-select-${this.type}`;
    }
    get getName(): string {
        return this.FwField.name;
    }
    get getInputValue(): string {
        return this.inputValue;
    }
    get getMultipleValue(): string {
        return Array.isArray(this.model) ?
            this.model.reduce((accum, cur) => {
                return accum ?
                    `${accum}, ${this.label ? cur[this.label] : cur}` :
                    `${this.label ? cur[this.label] : cur}`;
            }, '') :
            [];
    }

    get isPhoneWithCodeType(): boolean {
        return this.type === 'phone-with-code';
    }

    addListenerForScroll() {
        this.onFocus();
        if (this.pagination) {
            const el = this.$el.querySelector('.multiselect__content-wrapper');
            el!.addEventListener('scroll', this.checkScrolltoBottom);
        }
    }

    removeValue() {
        this.onDeselect(this.inputValue);
        this.inputValue = '';
        this.$emit('removeValue');
    }

    checkForSubstring() {
        this.countriesOptions.forEach(item => {
            if (this.phoneNumber.startsWith(item.prefix)) {
                this.localModel = item;
                const result = this.phoneNumber.replace(item.prefix, '');
                this.$emit('substrPhone', result, item.prefix);
            }
        });
    }
    checkScrolltoBottom(e: any) {
        if (Math.ceil(e.target.scrollTop + e.target.clientHeight) >= e.target.scrollHeight) {
            const el = this.$el.querySelector('.multiselect__content-wrapper');
            el!.removeEventListener('scroll', this.checkScrolltoBottom);
            this.$emit('goToNextPage');
        }
    }

    @Emit('select')
    onSelect(value: any): void {
        if (Array.isArray(value) && this.groupSelect) {
            if (value.length > 1) {
                this.model = value;
            } else {
                this.model.push(value[0]);
            }
        } else if (this.multiple) {
            const index = this.model.indexOf(value);
            // eslint-disable-next-line no-bitwise
            if (~index) {
                this.model.splice(index, 1);
            } else {
                this.model.push(value);
            }
        } else {
            this.model = value;
        }
        return value;
    }
    onInput(val: any): void {
        if (Array.isArray(val) && this.groupSelect) {
            if (!val.length) {
                this.model = [];
            } else {
                this.model = val;
            }
        }
    }
    onDeselect(value: any): void {
        if (this.canNotDelete) return;
        if (this.multiple) {
            if (this.groupSelect && Array.isArray(value)) {
                this.model = [];
            } else {
                let index = this.model.indexOf(value);

                if (this.isObject(value)) {
                    index = this.model.findIndex((item: any) => this.deepEqual(item, value));
                }

                if (~index) {
                    this.model.splice(index, 1);
                }
            }
        } else {
            this.model = null;
        }
    }

    isObject(value: any) {
        return value !== null && typeof value === 'object' && !Array.isArray(value) && typeof value !== 'function';
    }

    deepEqual(x:any, y:any): boolean {
        const ok = Object.keys; const tx = typeof x; const ty = typeof y;
        return x && y && tx === 'object' && tx === ty ? (
            ok(x).length === ok(y).length &&
            ok(x).every(key => this.deepEqual(x[key], y[key]))
        ) : (x === y);
    }

    @Emit('search-change')
    onChange(value: string) {
        this.inputValue = value;
    }
    @Watch('options')
    checkWrapperHeight() {
        this.$nextTick(() => {
            this.addListenerForScroll();
        });
    }

    async created() {
        if (this.isPhoneWithCodeType) {
            const countries = await import('../../libs/countryCodes');
            if (this.locales && this.locales.length) {
                const result = {} as CountriesType;
                for (const key in countries.default) {
                    if (this.locales.includes(key)) {
                        result[key] = countries.default[key];
                    }
                }

                this.countries = result;
            } else {
                this.countries = countries.default;
            }
        }
        if (this.phoneWithCodeStartValue && this.isPhoneWithCodeType) {
            this.localModel = this.countriesOptions.find(item => item.id === this.phoneWithCodeStartValue);
        } else {
            this.localModel = this.model;
        }
        if (this.tryToUseSubstring && this.phoneNumber) {
            this.checkForSubstring();
        }
    }
}
</script>

<style lang="sass">
$min-height: 50px
$min-height-white: 60px

li
    list-style-type: none

.fw-select-multiply,
.fw-select-base,
.fw-select-white,
.fw-select-address,
.fw-select-tooltip,
.fw-select-sort-simple
    .h-blue-color
        color: #35495e

    .multiselect__tags
        box-shadow: none !important
        height: $min-height
    .multiselect--active:after
        opacity: 1 !important
        transition: opacity .3s
        transition-delay: .1s

    .multiselect:after
        display: none

    fieldset[disabled] .multiselect
        pointer-events: none

    .multiselect__spinner
        position: absolute
        right: 1px
        top: 1px
        width: 48px
        height: 35px
        background: #fff
        display: block

        &:after
            position: absolute
            content: ""
            top: 50%
            left: 50%
            margin: -8px 0 0 -8px
            width: 16px
            height: 16px
            border-radius: 100%
            border-color: #70b93f transparent transparent
            border-style: solid
            border-width: 2px
            box-shadow: 0 0 0 1px transparent

        &:before
            position: absolute
            content: ""
            top: 50%
            left: 50%
            margin: -8px 0 0 -8px
            width: 16px
            height: 16px
            border-radius: 100%
            border-color: #70b93f transparent transparent
            border-style: solid
            border-width: 2px
            box-shadow: 0 0 0 1px transparent
            animation: a 2.4s cubic-bezier(0.41, 0.26, 0.2, 0.62)
            animation-iteration-count: infinite

        &:after
            animation: a 2.4s cubic-bezier(0.51, 0.09, 0.21, 0.8)
            animation-iteration-count: infinite

    .multiselect__loading-enter-active, .multiselect__loading-leave-active
        transition: opacity .4s ease-in-out
        opacity: 1

    .multiselect__loading-enter, .multiselect__loading-leave-active
        opacity: 0

    .multiselect, .multiselect__input, .multiselect__single
        font-family: inherit
        font-size: inherit
        -ms-touch-action: manipulation
        touch-action: manipulation

    .multiselect
        box-sizing: content-box
        display: block
        position: relative
        z-index: 4
        width: 100%
        text-align: left
        color: #35495e
        transition: z-index 0s .1s
        min-height: auto

        &:focus
            outline: none

    .multiselect--disabled
        cursor: not-allowed
        color: #c7cacd

    .multiselect--active
        transition: z-index 0s 0s
        z-index: 7

        &:not(.multiselect--above)
            .multiselect__content-wrapper
                z-index: 7

        .multiselect__tags,
        .multiselect__content-wrapper
            box-shadow: none
        .multiselect__tags
            position: relative
            z-index: 4

        .multiselect__select
            z-index: 4
.fw-multiselect-color
    .fw-select-white
        box-shadow: none!important
    .multiselect__single
        opacity: 0
    .fw-select-color__block
        width: 40px
        height: 20px
        border-radius: 5px
        .multiselect__single
            opacity: 0!important
    .fw-select-color__selected
        position: absolute
        z-index: 5
        top: 50%
        margin-top: -10px
        left: 20px
    .fw-select-white .multiselect__element, .fw-select-white .multiselect__tags, .fw-select-white .multiselect__option--selected
        border: none
        border-radius: 10px
        background-color: #F0F2F5!important
    .fw-select-white .multiselect__tags
        height: 50px
    .multiselect__option, .fw-select-address .multiselect__option, .fw-select-tooltip .multiselect__option
        padding: 5px 20px!important

.fw-select-base, .fw-select-address, .fw-select-tooltip
    .multiselect--above.multiselect--active
        .multiselect__current, .multiselect__input, .multiselect__tags
            border-top-left-radius: 0
            border-top-right-radius: 0
            border-bottom: 0 !important

    .multiselect--active
        transition: z-index 0s 0s
        z-index: 11
        &:not(.multiselect--above)
            .multiselect__current, .multiselect__input, .multiselect__tags
                border-bottom-left-radius: 0
                border-bottom-right-radius: 0

    .multiselect__input, .multiselect__single
        transition: border, background-color 0.5s ease
        position: relative
        min-height: $min-height
        line-height: 18px
        border-radius: 15px
        margin-bottom: 0
        color: $alt-blue
        border: none !important
        padding: 0 20px 0 0 !important
        background: transparent !important
        width: 100%
        display: flex
        align-items: center

        &:focus,
        &:active
            box-shadow: none !important

    .multiselect__tag ~ .multiselect__input,
    .multiselect__single
        width: auto

    .multiselect__input:focus
        border-color: #a8a8a8
        outline: none

    .multiselect__single
        &:focus
            border-color: #a8a8a8
            outline: none
        padding-left: 6px
        margin-bottom: 0

    .multiselect__tags-wrap
        display: inline

    .multiselect__tags
        transition: background-color 0.5s ease
        display: flex
        align-items: center
        background: $light-grey
        padding: 0 0 0 30px
        min-height: $min-height
        border-radius: 15px
        border: 2px solid $middle-gray
    &__time
        .multiselect__tags
            padding: 0 10px
        .fw-select__caret
            right: 10px
        .multiselect__option
            padding: 17px 10px!important
    &__week
        .multiselect__tags
            padding: 0 20px
        .multiselect__option
            padding: 17px 20px!important
    &__week, &__time
        .multiselect__tags
            color: $alt-blue
            font-weight: 400
            border: none
            border-radius: 10px
            max-height: 40px
            min-height: 40px
    .multiselect__tag
        position: relative
        display: inline-block
        padding: 2px 26px 2px 10px
        border-radius: 3px
        margin-right: 10px
        color: #fff
        line-height: 1
        background: #70b93f
        margin-bottom: 8px
        white-space: nowrap

    .multiselect__tag-icon
        cursor: pointer
        margin-left: 7px
        position: absolute
        right: 0
        top: 0
        bottom: 0
        font-weight: 700
        font-style: normal
        width: 22px
        text-align: center
        line-height: 15px
        transition: all .2s ease
        border-radius: 3px

        &:after
            content: "\D7"
            color: #266d4d
            font-size: 14px

        &:focus, &:hover
            background: #369a6e

        &:focus:after, &:hover:after
            color: #fff

    .multiselect__current
        min-height: 30px
        overflow: hidden
        padding: 8px 12px 0 30px
        white-space: nowrap
        line-height: 16px
        box-sizing: border-box
        display: block
        margin: 0
        text-decoration: none
        cursor: pointer

    .multiselect__select
        line-height: 16px
        display: block
        text-decoration: none
        cursor: pointer
        position: absolute
        width: 38px
        height: 24px
        right: 14px
        top: 0
        bottom: 0
        margin: auto
        padding: 4px 8px
        text-align: center
        transition: transform 0.2s ease

        &:before
            position: relative
            right: 0
            top: 65%
            color: #999
            margin-top: 4px
            border-style: solid
            border-width: 6px 6px 0
            border-color: #999 transparent transparent
            content: ""

    .multiselect__placeholder
        color: $hard-gray
        display: inline-block
        padding-top: 2px
        margin: 0 !important

    .multiselect__content-wrapper
        display: flex
        border: none
        box-sizing: content-box
        position: absolute
        background: $light-grey
        width: 100%
        max-height: 240px
        overflow: auto
        box-shadow: none
        z-index: 4
        -webkit-overflow-scrolling: touch
        border-radius: 0 0 15px 15px

    .multiselect__content
        list-style: none
        display: inline-block
        padding: 0
        margin: 0
        min-width: 100%
        vertical-align: top

        .multiselect__element:not(:first-of-type)
            .multiselect__option:after
                display: none !important
                // temp
                top: 0
                right: 0
                left: 0
                width: 80%
                position: absolute
                height: 1px
                content: ''
                margin: auto

    .multiselect--above .multiselect__content-wrapper
        bottom: 100%
        border-radius: 15px

    .multiselect__content::-webkit-scrollbar
        display: none

    .multiselect__element
        display: block
        background-color: $light-grey

        &:hover
            background-color: $middle-gray

        .multiselect__option--selected, &:hover
            color: $alt-blue

        .multiselect__option--highlight
            background-color: transparent

    .multiselect__option
        display: block
        min-height: 30px
        line-height: 16px
        text-decoration: none
        text-transform: none
        vertical-align: middle
        position: relative
        cursor: pointer
        word-wrap: break-word
        padding: 17px 32px

        /*&[data-selected]*/
        /*    font-weight: bold*/

        .fw-checkbox
            margin-bottom: 0
            margin-right: 10px

    .multiselect__option--highlight
        outline: none

    .multiselect__option--selected
        /*background: #f3f3f3*/
        font-weight: 700

        &:after
            content: attr(data-selected)
            color: silver

    .multiselect--disabled
        .multiselect__current,
        .multiselect__tags,
        .multiselect__input
            color: #c7cacd
            cursor: not-allowed

        .multiselect__input::placeholder
            color: #c7cacd

    .multiselect__option--disabled
        color: #a6a6a6
        cursor: text
        pointer-events: none

    .multiselect-enter-active, .multiselect-leave-active
        transition: all 0.15s ease

    .multiselect-enter, .multiselect-leave-active
        opacity: 0

    .multiselect__strong
        margin-bottom: 10px
        display: inline-block
.fw-select-base
    .multiselect
        color: $hard-gray
.fw-select-address
    .multiselect
        box-sizing: content-box
        display: flex
        flex-flow: column
        justify-content: flex-start
        align-items: center
        box-shadow: none !important
        .fw-select__caret
            height: 60px
        .multiselect__tags
            box-shadow: none
            width: 100%
    .multiselect__option
        padding: 0
    .multiselect:after
        display: none
    .fw-select-white, .fw-select-white-modern
        .multiselect__option--selected
            background: transparent!important
        .multiselect__content-wrapper
            width: auto
            margin-top: 20px
            border-radius: 0
            box-shadow: none!important
            background: transparent
            position: relative
            .multiselect__element
                background: transparent!important
            .multiselect__content-item
                padding: 17px 0
                display: flex
                flex-flow: row nowrap
                justify-content: space-between
                border-bottom: 1px solid #BEC7D4
                .multiselect__title-first
                    font-size: 18px
                    color: #213F6B
                    line-height: 22px
                    font-weight: bold
                    &:hover
                        color: $alt-blue-hover
                .multiselect__title-second
                    color: #213F6B
                    letter-spacing: 0.8px
                    line-height: 19px
                    text-transform: uppercase
                    &:hover
                        color: $alt-blue-hover
        .multiselect--above
            .multiselect__tags
                border-radius: 10px 10px 0 0 !important
    &__company
        .fw-select-white
            .multiselect__content-wrapper
                width: auto
    .fw-select-big-icon
      height: 60px
      width: 355px
      border-radius: 38px
      background-color: #F8F9FA
.fw-select-address.fw-select-address__absolute
    .multiselect__content-wrapper
        background: #fff
        border-radius: 0
        box-shadow: none !important
        position: absolute
        top: 40px
.fw-select-tooltip
    .multiselect__tags
        cursor: pointer
        background: transparent
        border: none
    .multiselect__element
        background: #ffffff
    .multiselect__content-wrapper
        display: flex
        border-radius: 25px
        background: #ffffff
        overflow: initial!important
        -webkit-overflow-scrolling: inherit
    .multiselect__content-wrapper:before
        content: " "
        border-left: solid transparent 10px
        border-right: solid transparent 10px
        border-bottom: solid #fff 10px
        top: -10px
        height: 0
        right: 21px
        position: absolute
        width: 0
    .fw-select__caret
        transform: none!important
    .multiselect__content
        overflow: auto!important
        -webkit-overflow-scrolling: touch!important
        border-radius: 25px
    .multiselect__placeholder
        color: $alt-blue
// multiply selector component
.fw-select-multiply
    .multiselect
        box-sizing: content-box
        display: block
        position: relative
        z-index: 4
        width: 100%
        min-height: 38px
        text-align: left
        color: #35495e
        transition: z-index 0s .1s
        *
            box-sizing: border-box
        &:focus
            outline: none
    .multiselect--active
        transition: z-index 0s 0s
        z-index: 11
        &:not(.multiselect--above)
            .multiselect__current, .multiselect__input, .multiselect__tags
                border-bottom-left-radius: 0
                border-bottom-right-radius: 0
            .multiselect__content-wrapper
                z-index: 6
        .multiselect__select
            transform: rotate(180deg)
        .multiselect__tags,
        .multiselect__content-wrapper
            box-shadow: 0 3px 5px 0 rgba(50,50,50,.15)
        .multiselect__tags
            position: relative
            z-index: 6
        .multiselect__select
            z-index: 7
    .multiselect--above.multiselect--active
        .multiselect__current, .multiselect__input, .multiselect__tags
            border-top-left-radius: 0
            border-top-right-radius: 0

    .multiselect__input, .multiselect__single
        transition: border, background-color 0.5s ease
        position: relative
        display: inline-block
        min-height: 20px
        line-height: 20px
        border-radius: 3px
        width: 100%
        box-sizing: border-box
        margin-bottom: 8px
        border: none !important
        padding: 0 !important
        background: transparent !important

        &:focus,
        &:active
            box-shadow: none !important
    .multiselect__content-wrapper
        position: absolute
        display: block
        background: #fff
        width: 100%
        max-height: 240px
        overflow: auto
        border: 1px solid #dee2e4
        border-top: none
        border-bottom-left-radius: 5px
        border-bottom-right-radius: 5px
        z-index: 4
        -webkit-overflow-scrolling: touch

    .multiselect__content
        list-style: none
        display: inline-block
        padding: 0
        margin: 0
        min-width: 100%
        vertical-align: top

    .multiselect--above .multiselect__content-wrapper
        bottom: 100%
        border-bottom-left-radius: 0
        border-bottom-right-radius: 0
        border-top-left-radius: 5px
        border-top-right-radius: 5px
        border-bottom: none
        border-top: 1px solid #dee2e4

    .multiselect__content::-webkit-scrollbar
        display: none

    .multiselect__element
        display: block

    .multiselect__option
        display: block
        padding: 11px 12px
        min-height: 30px
        line-height: 16px
        text-decoration: none
        text-transform: none
        vertical-align: middle
        position: relative
        cursor: pointer
        white-space: nowrap
        &:after
            top: 0
            right: 0
            position: absolute
            line-height: 38px
            padding-right: 12px
            padding-left: 20px

    .multiselect__option--highlight
        background: #f3f3f3
        outline: none
        &:after
            content: attr(data-select)
            background: #f3f3f3

    .multiselect__option--selected
        background: #f3f3f3
        font-weight: 700
        &:after
            content: attr(data-selected)
            color: silver
        &.multiselect__option--highlight
            background: #f3f3f3
            &:after
                background: #f3f3f3
                content: attr(data-deselect)
    .multiselect__tags
        transition: background-color 0.5s ease
        min-height: 38px
        display: block
        padding: 8px 40px 0 8px
        border-radius: 3px
        border: 1px solid #dee2e4
        background: #fff

    .multiselect__tag
        position: relative
        display: inline-block
        padding: 4px 26px 4px 10px
        border-radius: 3px
        margin-right: 10px
        color: #fff
        line-height: 1
        background: #70b93f
        margin-bottom: 8px
        white-space: nowrap
    .multiselect__select
        line-height: 16px
        box-sizing: border-box
        display: block
        margin: 0
        text-decoration: none
        cursor: pointer
        position: absolute
        width: 38px
        height: 36px
        right: 1px
        top: 1px
        padding: 4px 8px
        text-align: center
        transition: transform 0.2s ease
        &:before
            position: relative
            right: 0
            top: 65%
            color: #999
            margin-top: 4px
            border-style: solid
            border-width: 5px 5px 0
            border-color: #999 transparent transparent
            content: ""
// multiply selector simple component
.fw-select-multiply-simple
    .multiselect
        width: auto!important
        height: 36px
        min-height: 36px
        box-sizing: content-box
        display: block
        position: relative
        z-index: 4
        text-align: left
        color: #35495e
        transition: z-index 0s .1s
        *
            box-sizing: border-box
        &:focus
            outline: none
    .multiselect--active
        transition: z-index 0s 0s
        z-index: 11
        &:not(.multiselect--above)
            .multiselect__current, .multiselect__input, .multiselect__tags
                border-bottom-left-radius: 0
                border-bottom-right-radius: 0
            .multiselect__content-wrapper
                z-index: 6
        .multiselect__select
            height: 10px
            right: -5px
            top: 15px
            transform: rotate(180deg)
        .multiselect__tags,
        .multiselect__content-wrapper
            position: relative!important
            color: rgba(33, 63, 107, 0.3)!important
            background: #F6F8F9!important
            width: auto!important
            max-width: 130px!important
            border: none!important
            box-shadow: 0 3px 5px 0 rgba(50,50,50,.15)
        .multiselect__tags
            position: relative
            z-index: 6
        .multiselect__select
            z-index: 7
    .multiselect--above.multiselect--active
        .multiselect__current, .multiselect__input, .multiselect__tags
            border-top-left-radius: 0
            border-top-right-radius: 0

    .multiselect__input, .multiselect__single
        transition: border, background-color 0.5s ease
        position: relative
        display: inline-block
        min-height: 20px
        line-height: 20px
        border: none
        border-radius: 3px
        width: 100%
        box-sizing: border-box
        margin-bottom: 8px
        padding: 0 !important
        background: transparent !important

        &:focus,
        &:active
            box-shadow: none !important
    .multiselect__content-wrapper
        width: auto!important
        overflow: hidden!important
        border-radius: 0 0 10px 10px!important
        position: absolute
        background: #fff
        max-height: 240px
        border: none
        border-top: none
        z-index: 4
        -webkit-overflow-scrolling: touch
    .multiselect__content
        padding: 0 10px 6px
        background: #fff
        list-style: none
        display: inline-block
        margin: 0
        min-width: 130px
        vertical-align: top

    .multiselect--above .multiselect__content-wrapper
        bottom: 100%
        border-bottom-left-radius: 0
        border-bottom-right-radius: 0
        border-top-left-radius: 5px
        border-top-right-radius: 5px
        border-bottom: none
        border-top: 1px solid #dee2e4
    .multiselect__content::-webkit-scrollbar
        display: none

    .multiselect__element
        display: flex!important
        flex-flow: column nowrap!important
        justify-content: center!important
        align-items: center!important
        background: #fff!important
        border: none!important
        color: #213F6B
        font-size: 12px
        font-weight: bold
        letter-spacing: 0.43px
        line-height: 14px
    .multiselect__option
        border: none!important
        padding: 0 0 0 15px!important
        display: flex!important
        flex-flow: row nowrap!important
        justify-content: flex-start!important
        align-items: center!important
        margin: 3px 0
        border-radius: 5px
        background-color: #F7F8FA
        height: 36px
        width: 100%
        min-height: 36px
        line-height: 16px
        text-decoration: none
        text-transform: none
        vertical-align: middle
        position: relative
        cursor: pointer
        white-space: nowrap
        &:after
            top: 0
            right: 0
            position: absolute
            line-height: 38px
            padding-right: 12px
            padding-left: 20px

    .fw-select .multiselect__element .multiselect__option--selected,
    .fw-select .multiselect__element .multiselect__option--highlight
        border-radius: 5px
        background-color: #27DBBD
        height: 36px
        width: 100%
        color: #fff!important
        outline: none
        &:after
            content: attr(data-select)
            background: #f3f3f3
    .fw-select .multiselect__option--highlight:hover, .multiselect__option--highlight
        background-color: #27DBBD

    .multiselect__option--selected
        border-radius: 5px
        background-color: #27DBBD
        height: 36px
        width: auto
        color: #fff
        font-weight: 700
        &:after
            content: attr(data-selected)
            color: silver
        &.multiselect__option--highlight
            border-radius: 5px
            background-color: #27DBBD
            height: 36px
            width: 120px
            color: #fff
            &:after
                background: #f3f3f3
                content: attr(data-deselect)
    .fw-select .multiselect__tags
        cursor: pointer
        display: flex
        flex-flow: row nowrap
        justify-content: space-between
        align-items: center
        border-radius: 5px 5px 0 0
        width: 130px
        background: #fff
        border: none
        color: #213F6B
        font-size: 12px
        font-weight: bold
        letter-spacing: 0.43px
        line-height: 14px
        padding: 0 0 0 12px
        height: 36px
        min-height: 36px
        transition: background-color 0.5s ease

    .multiselect__tag
        position: relative
        display: inline-block
        padding: 4px 26px 4px 10px
        border-radius: 3px
        margin-right: 10px
        color: #fff
        line-height: 1
        background: #70b93f
        margin-bottom: 8px
        white-space: nowrap
    .fw-select .multiselect__select
        z-index: 7
        right: -5px
        line-height: 16px
        box-sizing: border-box
        display: block
        margin: 0
        text-decoration: none
        cursor: pointer
        position: absolute
        width: 38px
        height: 20px
        margin-top: -10px
        top: 50%
        padding: 4px 8px
        text-align: center
        transition: transform 0.2s ease
        &:before
            position: relative
            right: 0
            top: 65%
            color: #999
            margin-top: 4px
            border-style: solid
            border-width: 5px 5px 0
            border-color: #999 transparent transparent
            content: ""
    .fw-select .multiselect__content .multiselect__element:not(:first-of-type) .multiselect__option:after
        height: 0!important
        background: none!important
        border: none!important
    .value-data
        position: absolute
        top: 5px
        left: 7px
        z-index: 1000
        margin: 5px
        color: #213F6B
        font-size: 12px
        font-weight: bold
        letter-spacing: 0.43px
        line-height: 14px

    .fw-horizontal-arrow__right
        position: fixed
    .multiselect--active .multiselect__placeholder
        display: flex

    .multiselect__single
        &:focus
            border-color: #a8a8a8
            outline: none
        color: rgba(33, 63, 107, 0.3)
        padding-left: 6px
        margin-bottom: 0
    .multiselect__placeholder
        color: rgba(33, 63, 107, 0.3)
        display: inline-block
        margin-bottom: 0
        padding-top: 2px

    .multiselect__tag ~ .multiselect__input,
    .multiselect__single
        width: auto

    .multiselect__input:hover, .multiselect__single:hover
        border-color: #cfcfcf

    .multiselect__input:focus
        border-color: #a8a8a8
        outline: none

    .multiselect__single
        &:focus
            border-color: #a8a8a8
            outline: none
        padding-left: 6px
        margin-bottom: 8px

    .multiselect__tags-wrap
        display: inline

    .multiselect__tag-icon
        cursor: pointer
        margin-left: 7px
        position: absolute
        right: 0
        top: 0
        bottom: 0
        font-weight: 700
        font-style: normal
        width: 22px
        text-align: center
        line-height: 22px
        transition: all .2s ease
        border-radius: 3px
        &:after
            content: "\D7"
            color: #266d4d
            font-size: 14px
        &:focus, &:hover
            background: #369a6e
        &:focus:after, &:hover:after
            color: #fff
.fw-select-sort-simple
    color: $alt-blue!important
    .multiselect__tags
        background-color: transparent
        border: none !important
        border-radius: 10px
        padding-left: 12px

    &.multiselect--active
        .multiselect__tags
            transition-duration: 0.2s !important
            background-color: #ffffff

    .multiselect__element
        background-color: #ffffff

    .multiselect__option--selected
        &:after
            display: none !important

    .multiselect__option
        padding: 10px 12px
        font-size: 14px
        font-weight: 600

    .multiselect__single
        padding-top: 10px !important
        font-weight: 600
.fw-select-event-popup
    background-color: #F7F8FA
    padding-left: 15px
    border-radius: 10px
    color: $alt-blue!important
    .fw-select-form
        display: flex
        flex-flow: row nowrap
        justify-content: flex-start
        align-items: center
    .fw-select__right--icon
        position: relative
        left: 0
    .multiselect__content-wrapper
        top: 50px
    .multiselect__tags
        border: none !important
        padding-left: 12px

    &.multiselect--active
        .multiselect__tags
            transition-duration: 0.2s !important
            background-color: #F7F8FA
    .multiselect__element
        background-color: #F7F8FA

    .multiselect__option--selected
        &:after
            display: none !important

    .multiselect__option
        padding: 10px 12px
        font-size: 14px
        font-weight: 600

    .multiselect__single
        padding-top: 10px !important
        font-weight: 600
.fw-multiselect__title--sort-simple
    color: $title-gray
    font-size: 12px
    font-weight: 600
    position: absolute
    top: 5px
    left: 12px
    z-index: 12

@keyframes a
    0%
        transform: rotate(0)

    to
        transform: rotate(2turn)

.multiselect-option__checkbox
    display: flex

.fw-select-white
    box-shadow: $big-box-shadow !important
    .multiselect__element, .multiselect__tags, .multiselect__option--selected
        background-color: #fff !important
    .multiselect__tags
        border: none !important
        height: $min-height-white
        border-radius: 10px
.fw-form-input__wrapper
    .fw-select-white
        box-shadow: none !important

    .fw-select-white
        .fw-select:not(.fw-select-phone-with-code)
            .multiselect__tags
                min-height: 60px !important
.fw-select-white .multiselect__content-wrapper
    box-shadow: $big-box-shadow !important

.fw-select-multiply .multiselect__option:after
    min-height: $min-height !important
    height: $min-height !important

.multiselect__option--group.multiselect__option:before
    display: none
.multiselect__option--group.multiselect__option:after
    display: none
.multiselect--has-options-group .multiselect__option:after
    display: none

.fw-select__caret
    position: absolute
    z-index: 6
    right: 25px
    top: 0
    bottom: 0
    display: flex
    align-items: center
    transition: transform 0.2s ease-in-out

.multiselect--active
    .fw-select__caret--rotate
        transform: rotate(180deg)

.fw-select-auto-height
    .multiselect__tags
        height: auto !important

.fw-select-country-code
    display: flex
    align-items: center
    &__prefix
        font-size: 14px
        font-weight: 500

    &__image
        width: 20px
        margin-right: 5px

    .multiselect__content
        padding-left: 0 !important

.fw-select-phone-with-code
    .multiselect__tags, .multiselect__option
        padding: 20px!important
        padding-right: 0!important
    .fw-select__caret
        top: 8px
        right: 10px
    .multiselect__single
        padding-right: 5px!important
    .multiselect__placeholder, .multiselect__input
        font-size: 14px

.fw-hide-right-icon
    .multiselect__select
        display: none !important

    .multiselect__input, .multiselect__single
        padding: 0 0 0 0 !important

.fw-select-white-modern
    box-shadow: 0 30px 11px -17px rgba(33, 63, 107, 0.05) !important
    span.multiselect__placeholder
        color: rgba(33,63,107,0.5)
        font-weight: 500
        max-width: 75%
        text-overflow: ellipsis
        overflow: hidden
        white-space: nowrap
    .multiselect__content
        .multiselect__option
            padding: 17px 20px
            font-weight: 500
    .multiselect__tags
        padding: 0 0 0 20px
        .multiselect__single
            font-weight: 500
            color: $alt-blue

.fw-select-with-icon
    .multiselect__option
        padding: 10px 40px 10px 18px
        font-weight: 500
        position: relative
        &:before
            content: ''
            position: absolute
            right: 18px
            width: 13px
            height: 13px
            border-radius: 50%
            border: 1px solid #203F6A
        &.multiselect__option--selected
            &:before
                background-color: #27DBBD
                border-color: #27DBBD

    &.fw-select-with-icon--big
       .fw-select-white, .multiselect__tags
            height: 60px

    .fw-select-white, .multiselect__tags
        height: 50px

    .multiselect__input::placeholder, .multiselect__placeholder
        font-size: 14px
        font-weight: 500
        color: #213F6B

    .multiselect__option
        color: #213F6B
        &:hover
            opacity: 0.8

    .multiselect__option--selected
        font-weight: 600

    .multiselect__single
        font-weight: 500
        width: 100%
    .multiselect__content
        width: 100%
        .multiselect__option
            max-width: 100%
            overflow: hidden
            text-overflow: ellipsis
.multiselect__spinner
    z-index: 7 !important
    position: absolute
    right: 1px
    top: 50%!important
    margin-top: -20px
    width: 50px
    height: 40px!important
    background: #fff
    display: block
.fw-select__caret + .multiselect__tags .multiselect__spinner
    z-index: 7 !important
.fw-select-disabled
    opacity: 0.5

.h-text-overflow-ellipsis
    width: 100%
    text-overflow: ellipsis
    overflow: hidden
    white-space: nowrap
    display: block
</style>
